/**
 * PIN 관리 섹션 컴포넌트
 * 마스터 PIN 설정 및 PIN 보호 옵션 관리
 */

import React, { useState, useEffect } from 'react';
import { usePin } from '../../contexts/PinContext';
import { Button } from '../UI';
import { 
  Lock, 
  Unlock, 
  Shield, 
  Key,
  Settings,
  Check,
  X,
  AlertCircle,
  Eye,
  EyeOff
} from 'lucide-react';
import './PinManagementSection.css';

function PinManagementSection() {
  const {
    pinSettings,
    isUnlocked,
    loading,
    error: contextError,
    setMasterPin,
    verifyMasterPin,
    changeMasterPin,
    removeMasterPin,
    updatePinSettings,
    lock
  } = usePin();

  const [mode, setMode] = useState('view'); // view, set, change, remove
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [localSettings, setLocalSettings] = useState({
    autoLockMinutes: 5
  });

  useEffect(() => {
    setLocalSettings({
      autoLockMinutes: pinSettings.autoLockMinutes || 5
    });
  }, [pinSettings]);

  // PIN 입력 검증
  const validatePin = (pin) => {
    if (pin.length !== 6) {
      return 'PIN은 6자리여야 합니다.';
    }
    if (!/^\d{6}$/.test(pin)) {
      return 'PIN은 숫자만 입력 가능합니다.';
    }
    return null;
  };

  // 마스터 PIN 설정
  const handleSetPin = async () => {
    setError('');
    
    const newPinError = validatePin(newPin);
    if (newPinError) {
      setError(newPinError);
      return;
    }
    
    if (newPin !== confirmPin) {
      setError('PIN이 일치하지 않습니다.');
      return;
    }
    
    try {
      await setMasterPin(newPin);
      setSuccess('마스터 PIN이 설정되었습니다.');
      setMode('view');
      resetForm();
    } catch (err) {
      setError('PIN 설정에 실패했습니다.');
    }
  };

  // 마스터 PIN 변경
  const handleChangePin = async () => {
    setError('');
    
    const currentPinError = validatePin(currentPin);
    if (currentPinError) {
      setError('현재 ' + currentPinError);
      return;
    }
    
    const newPinError = validatePin(newPin);
    if (newPinError) {
      setError('새 ' + newPinError);
      return;
    }
    
    if (newPin !== confirmPin) {
      setError('새 PIN이 일치하지 않습니다.');
      return;
    }
    
    if (currentPin === newPin) {
      setError('새 PIN은 현재 PIN과 달라야 합니다.');
      return;
    }
    
    try {
      await changeMasterPin(currentPin, newPin);
      setSuccess('마스터 PIN이 변경되었습니다.');
      setMode('view');
      resetForm();
    } catch (err) {
      setError(err.message || 'PIN 변경에 실패했습니다.');
    }
  };

  // 마스터 PIN 제거
  const handleRemovePin = async () => {
    setError('');
    
    const currentPinError = validatePin(currentPin);
    if (currentPinError) {
      setError(currentPinError);
      return;
    }
    
    if (!window.confirm('마스터 PIN을 제거하시겠습니까?\n모든 PIN 보호가 해제됩니다.')) {
      return;
    }
    
    try {
      await removeMasterPin(currentPin);
      setSuccess('마스터 PIN이 제거되었습니다.');
      setMode('view');
      resetForm();
    } catch (err) {
      setError(err.message || 'PIN 제거에 실패했습니다.');
    }
  };

  // PIN 보호 설정 업데이트
  const handleSettingChange = async (key, value) => {
    const newSettings = { ...localSettings, [key]: value };
    setLocalSettings(newSettings);
    
    try {
      await updatePinSettings(newSettings);
      setSuccess('설정이 업데이트되었습니다.');
    } catch (err) {
      setError('설정 업데이트에 실패했습니다.');
      // 실패시 원래 값으로 복구
      setLocalSettings(prev => ({ ...prev, [key]: !value }));
    }
  };

  // 폼 초기화
  const resetForm = () => {
    setCurrentPin('');
    setNewPin('');
    setConfirmPin('');
    setShowPin(false);
    setTimeout(() => {
      setSuccess('');
    }, 3000);
  };

  // 모드 변경시 초기화
  const handleModeChange = (newMode) => {
    setMode(newMode);
    resetForm();
    setError('');
    setSuccess('');
  };

  return (
    <div className="pin-management-section">
      {/* 알림 메시지 */}
      {(error || contextError) && (
        <div className="pin-alert pin-alert-error">
          <AlertCircle size={16} />
          <span>{error || contextError}</span>
        </div>
      )}
      
      {success && (
        <div className="pin-alert pin-alert-success">
          <Check size={16} />
          <span>{success}</span>
        </div>
      )}

      {/* 마스터 PIN 상태 */}
      <div className="pin-status-card">
        <div className="pin-status-header">
          <div className="pin-status-info">
            <Shield size={24} className={pinSettings.hasMasterPin ? 'icon-active' : 'icon-inactive'} />
            <div>
              <h3>마스터 PIN</h3>
              <p className="pin-status-text">
                {pinSettings.hasMasterPin 
                  ? (isUnlocked ? '설정됨 (잠금 해제됨)' : '설정됨 (잠김)') 
                  : '설정되지 않음'}
              </p>
            </div>
          </div>
          
          <div className="pin-status-actions">
            {pinSettings.hasMasterPin ? (
              <>
                {isUnlocked && (
                  <Button variant="secondary" size="sm" onClick={lock}>
                    <Lock size={14} />
                    잠금
                  </Button>
                )}
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={() => handleModeChange('change')}
                  disabled={mode !== 'view'}
                >
                  PIN 변경
                </Button>
                <Button 
                  variant="danger" 
                  size="sm" 
                  onClick={() => handleModeChange('remove')}
                  disabled={mode !== 'view'}
                >
                  PIN 제거
                </Button>
              </>
            ) : (
              <Button 
                variant="primary" 
                size="sm" 
                onClick={() => handleModeChange('set')}
                disabled={mode !== 'view'}
              >
                <Key size={14} />
                PIN 설정
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* PIN 설정/변경/제거 폼 */}
      {mode !== 'view' && (
        <div className="pin-form-card">
          <h4 className="pin-form-title">
            {mode === 'set' && '마스터 PIN 설정'}
            {mode === 'change' && '마스터 PIN 변경'}
            {mode === 'remove' && '마스터 PIN 제거'}
          </h4>

          <div className="pin-form-fields">
            {(mode === 'change' || mode === 'remove') && (
              <div className="pin-input-group">
                <label>현재 PIN</label>
                <div className="pin-input-wrapper">
                  <input
                    type={showPin ? 'text' : 'password'}
                    value={currentPin}
                    onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="6자리 숫자"
                    maxLength="6"
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className="pin-visibility-toggle"
                    onClick={() => setShowPin(!showPin)}
                  >
                    {showPin ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            )}

            {(mode === 'set' || mode === 'change') && (
              <>
                <div className="pin-input-group">
                  <label>{mode === 'change' ? '새 PIN' : 'PIN'}</label>
                  <div className="pin-input-wrapper">
                    <input
                      type={showPin ? 'text' : 'password'}
                      value={newPin}
                      onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="6자리 숫자"
                      maxLength="6"
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="pin-input-group">
                  <label>PIN 확인</label>
                  <div className="pin-input-wrapper">
                    <input
                      type={showPin ? 'text' : 'password'}
                      value={confirmPin}
                      onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="6자리 숫자"
                      maxLength="6"
                      autoComplete="off"
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="pin-form-actions">
            <Button 
              variant="secondary" 
              onClick={() => handleModeChange('view')}
              disabled={loading}
            >
              취소
            </Button>
            <Button 
              variant="primary" 
              onClick={() => {
                if (mode === 'set') handleSetPin();
                else if (mode === 'change') handleChangePin();
                else if (mode === 'remove') handleRemovePin();
              }}
              disabled={loading}
            >
              {loading ? '처리중...' : (
                mode === 'set' ? '설정' :
                mode === 'change' ? '변경' :
                '제거'
              )}
            </Button>
          </div>
        </div>
      )}

      {/* PIN 보호 설정 */}
      {pinSettings.hasMasterPin && (
        <div className="pin-settings-card">
          <h4 className="pin-settings-title">PIN 보호 설정</h4>
          
          <div className="pin-setting-info">
            <Shield size={20} />
            <p>마스터 PIN이 설정되어 있습니다.</p>
            <p className="pin-setting-description">
              PIN 보호를 설정한 모든 항목은 마스터 PIN으로 보호됩니다.
            </p>
          </div>

          <div className="pin-setting-item">
            <div className="pin-setting-label">
              <span>자동 잠금</span>
              <p className="pin-setting-description">
                일정 시간 후 자동으로 PIN을 다시 요구합니다
              </p>
            </div>
            <select 
              className="pin-select"
              value={localSettings.autoLockMinutes}
              onChange={(e) => handleSettingChange('autoLockMinutes', Number(e.target.value))}
              disabled={loading}
            >
              <option value="1">1분</option>
              <option value="5">5분</option>
              <option value="15">15분</option>
              <option value="30">30분</option>
              <option value="60">1시간</option>
              <option value="-1">사용 안 함</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

export default PinManagementSection;